import { S25Util } from "../../util/s25-util";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";

export const RECIPIENT_LIST_MAX = 2000;

export interface RecipientListParseResult {
    validEmails: string[];
    invalidCount: number;
    duplicateCount: number;
    cappedCount: number;
}

const EMAIL_HEADER_ALIASES = new Set(["email", "e_mail", "contact_email", "email_address", "e-mail", "mail"]);

function normalizeHeader(cell: string): string {
    return cell.trim().toLowerCase().replace(/\s+/g, "_");
}

function isEmailHeader(cell: string): boolean {
    return EMAIL_HEADER_ALIASES.has(normalizeHeader(cell));
}

function collectEmailsFromTokens(tokens: string[]): {
    validEmails: string[];
    invalidCount: number;
    duplicateCount: number;
} {
    const validEmails: string[] = [];
    const seenLower = new Set<string>();
    let invalidCount = 0;
    let duplicateCount = 0;

    for (const raw of tokens) {
        const token = S25Util.trim(raw);
        if (!token) continue;
        if (!S25Util.isValidEmail(token)) {
            invalidCount++;
            continue;
        }
        const lower = token.toLowerCase();
        if (seenLower.has(lower)) {
            duplicateCount++;
            continue;
        }
        seenLower.add(lower);
        validEmails.push(token);
    }

    return { validEmails, invalidCount, duplicateCount };
}

function applyMaxCap(validEmails: string[], invalidCount: number, duplicateCount: number): RecipientListParseResult {
    if (validEmails.length <= RECIPIENT_LIST_MAX) {
        return { validEmails, invalidCount, duplicateCount, cappedCount: 0 };
    }
    const cappedCount = validEmails.length - RECIPIENT_LIST_MAX;
    return {
        validEmails: validEmails.slice(0, RECIPIENT_LIST_MAX),
        invalidCount,
        duplicateCount,
        cappedCount,
    };
}

function splitCsvLine(line: string): string[] {
    return line.split(/[,;\t]/).map((cell) => cell.trim());
}

function tokenizeDelimitedText(text: string): string[] {
    return text
        .split(/[,;\s\n\r]+/)
        .map(S25Util.trim)
        .filter(S25Util.isTruthy);
}

/** Paste, .txt, and .csv — header row with an email column, or delimited tokens. */
export function parseRecipientListText(text: string): RecipientListParseResult {
    const trimmed = text?.trim() ?? "";
    if (!trimmed) {
        return { validEmails: [], invalidCount: 0, duplicateCount: 0, cappedCount: 0 };
    }

    const lines = trimmed.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (lines.length > 1) {
        const headerCells = splitCsvLine(lines[0]);
        const emailColumnIndex = headerCells.findIndex((cell) => isEmailHeader(cell));
        if (emailColumnIndex >= 0) {
            const columnTokens: string[] = [];
            for (let rowIndex = 1; rowIndex < lines.length; rowIndex++) {
                const cells = splitCsvLine(lines[rowIndex]);
                const value = cells[emailColumnIndex];
                if (value) columnTokens.push(value);
            }
            const collected = collectEmailsFromTokens(columnTokens);
            return applyMaxCap(collected.validEmails, collected.invalidCount, collected.duplicateCount);
        }
    }

    const collected = collectEmailsFromTokens(tokenizeDelimitedText(trimmed));
    return applyMaxCap(collected.validEmails, collected.invalidCount, collected.duplicateCount);
}

/** First sheet rows from SheetJS `sheet_to_row_object_array`. */
export function parseRecipientEmailsFromXlsxRows(rows: Record<string, unknown>[]): RecipientListParseResult {
    if (!rows?.length) {
        return { validEmails: [], invalidCount: 0, duplicateCount: 0, cappedCount: 0 };
    }

    const keys = Object.keys(rows[0] ?? {});
    const emailKey = keys.find((key) => isEmailHeader(key));
    const tokens: string[] = [];

    if (emailKey) {
        for (const row of rows) {
            const value = row[emailKey];
            if (value != null && String(value).trim()) {
                tokens.push(String(value));
            }
        }
    } else {
        const firstKey = keys[0];
        if (firstKey) {
            for (const row of rows) {
                const value = row[firstKey];
                if (value != null && String(value).trim()) {
                    tokens.push(String(value));
                }
            }
        }
    }

    const collected = collectEmailsFromTokens(tokens);
    return applyMaxCap(collected.validEmails, collected.invalidCount, collected.duplicateCount);
}

export async function parseRecipientEmailsFromXlsxFile(file: File): Promise<RecipientListParseResult> {
    const sheets = await SpreadSheetService.readExcelFile<Record<string, unknown>[][]>(file);
    const firstSheet = sheets?.[0] ?? [];
    return parseRecipientEmailsFromXlsxRows(firstSheet);
}

export async function parseRecipientEmailsFromFile(file: File): Promise<RecipientListParseResult> {
    const lowerName = file.name.toLowerCase();
    if (lowerName.endsWith(".xlsx")) {
        return parseRecipientEmailsFromXlsxFile(file);
    }
    if (lowerName.endsWith(".csv") || lowerName.endsWith(".txt")) {
        const text = await readFileAsText(file);
        return parseRecipientListText(text);
    }
    throw new Error("Unsupported file type. Use .csv, .txt, or .xlsx.");
}

function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(new Error("Could not read file"));
        reader.readAsText(file);
    });
}
